import { Fragment, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from 'components/card';
import GoBack from 'components/actions/GoBack';
import ShopifyMenu from 'views/shopify/components/ShopifyMenu';
import SearchIcon from 'components/icons/SearchIcon';
import fetchClient from 'interfaces/fetchClient';
import env from 'variables/config';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TABS: { key: string; label: string }[] = [
    { key: '', label: 'Toutes' },
    { key: 'pending', label: 'En attente' },
    { key: 'partial', label: 'Partielles' },
    { key: 'applied', label: 'Appliquées' },
    { key: 'reverted', label: 'Annulées' },
    { key: 'historical', label: 'Antérieures à la connexion' },
];

const STATUS_BADGE: Record<string, string> = {
    applied: 'bg-green-100 text-green-700',
    partial: 'bg-amber-100 text-amber-700',
    pending: 'bg-red-100 text-red-700',
    reverted: 'bg-gray-100 text-gray-500',
    historical: 'bg-blue-50 text-blue-600',
};

const formatDate = (iso?: string) =>
    iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const BACKFILL_OPTIONS = [
    { days: 30, label: '30 jours' },
    { days: 90, label: '90 jours' },
    { days: 180, label: '6 mois' },
    { days: 365, label: '1 an' },
    { days: 730, label: '2 ans' },
];

const PAGE_SIZE = 50;

const ShopifyOrders = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeStatus = searchParams.get('stockSyncStatus') || '';
    const [items, setItems] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [retrying, setRetrying] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [backfillDays, setBackfillDays] = useState(365);
    const [backfilling, setBackfilling] = useState(false);

    const fetchItems = useCallback(() => {
        setLoading(true);
        const params: any = { limit: PAGE_SIZE, page };
        if (activeStatus) params.stockSyncStatus = activeStatus;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        fetchClient
            .get(`${env.apiUrl}/shopify/orders`, { ...authHeaders(), params })
            .then((res) => {
                setItems(res.data.items || []);
                setTotal(res.data.total || 0);
            })
            .catch(() => toast.error('Erreur lors du chargement des commandes Shopify.'))
            .finally(() => setLoading(false));
    }, [activeStatus, dateFrom, dateTo, page]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const retry = async (orderId: string) => {
        setRetrying(orderId);
        try {
            await fetchClient.post(`${env.apiUrl}/shopify/orders/${orderId}/retry-stock-sync`, {}, authHeaders());
            toast.success('Synchronisation du stock relancée.');
            fetchItems();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Erreur lors de la relance.');
        } finally {
            setRetrying(null);
        }
    };

    const backfill = async () => {
        setBackfilling(true);
        try {
            const res = await fetchClient.post(`${env.apiUrl}/shopify/orders/backfill`, { days: backfillDays }, authHeaders());
            toast.success(res.data.message || 'Import historique lancé.');
            setTimeout(fetchItems, 5000);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Erreur lors du lancement de l'import.");
        } finally {
            setBackfilling(false);
        }
    };

    const searchLower = search.trim().toLowerCase();
    const filteredItems = searchLower
        ? items.filter((order: any) => {
              const haystacks = [
                  order.orderNumber,
                  order.customerName,
                  order.customerEmail,
                  ...(order.lineItems || []).flatMap((li: any) => [li.title, li.sku]),
              ];
              return haystacks.filter(Boolean).some((field: string) => field.toLowerCase().includes(searchLower));
          })
        : items;

    return (
        <>
            <GoBack />
            <ShopifyMenu />
            <Card extra="w-full h-full sm:overflow-auto p-5 border mt-5">
                <div className="mb-5">
                    <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Commandes Shopify</h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Commandes importées depuis Shopify. Elles n'entrent pas dans le flux de commandes Toola
                        (préparation, tournées, facturation) — cet écran est informatif et permet de suivre la
                        synchronisation du stock. La synchro régulière ne remonte que les commandes récentes ; utilisez
                        l'import ci-dessous pour aller chercher un historique plus large. Les commandes antérieures à
                        la connexion Shopify ne décrémentent jamais le stock, y compris via cet import — cette vente
                        a déjà eu lieu avant que Toola ne suive le stock.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <select
                            value={backfillDays}
                            onChange={(e) => setBackfillDays(Number(e.target.value))}
                            className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 outline-none dark:border-navy-700 dark:bg-navy-900 dark:text-gray-300"
                        >
                            {BACKFILL_OPTIONS.map((opt) => (
                                <option key={opt.days} value={opt.days}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={backfill}
                            disabled={backfilling}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-700 dark:text-gray-300 dark:hover:bg-navy-700"
                        >
                            {backfilling ? 'Lancement...' : "Importer l'historique"}
                        </button>
                        <span className="text-xs text-gray-400">
                            Shopify ne renvoie que les 60 derniers jours sauf si le scope <code>read_all_orders</code> a été activé.
                        </span>
                    </div>
                </div>

                <div className="mb-5 flex flex-wrap gap-2">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => {
                                setSearchParams(tab.key ? { stockSyncStatus: tab.key } : {});
                                setPage(1);
                            }}
                            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                                activeStatus === tab.key
                                    ? 'bg-navy-700 text-white dark:bg-white dark:text-navy-700'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="mb-5 flex flex-wrap items-center gap-3">
                    <div className="flex h-[38px] max-w-md flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Rechercher sur cette page (n° commande, client, article, SKU)..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-500">Du</label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => {
                                setDateFrom(e.target.value);
                                setPage(1);
                            }}
                            className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-gray-600 outline-none dark:border-navy-700 dark:bg-navy-900 dark:text-gray-300"
                        />
                        <label className="text-xs font-medium text-gray-500">au</label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => {
                                setDateTo(e.target.value);
                                setPage(1);
                            }}
                            className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-gray-600 outline-none dark:border-navy-700 dark:bg-navy-900 dark:text-gray-300"
                        />
                        {(dateFrom || dateTo) && (
                            <button
                                onClick={() => {
                                    setDateFrom('');
                                    setDateTo('');
                                    setPage(1);
                                }}
                                className="text-xs font-medium text-gray-400 hover:text-gray-600"
                            >
                                Réinitialiser
                            </button>
                        )}
                    </div>
                </div>

                {loading && <div className="py-10 text-center text-gray-500">Chargement...</div>}
                {!loading && items.length === 0 && (
                    <div className="py-10 text-center text-gray-500">Aucune commande dans cette catégorie.</div>
                )}
                {!loading && items.length > 0 && filteredItems.length === 0 && (
                    <div className="py-10 text-center text-gray-500">Aucune commande trouvée pour "{search}".</div>
                )}

                {!loading && filteredItems.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full text-left text-sm text-gray-500">
                            <thead className="bg-gray-50 text-gray-700">
                                <tr>
                                    <th className="px-4 py-3">Commande</th>
                                    <th className="px-4 py-3">Client</th>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Total</th>
                                    <th className="px-4 py-3">Stock</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((order: any) => (
                                    <Fragment key={order._id}>
                                        <tr
                                            className="cursor-pointer border-b bg-white hover:bg-gray-50 dark:border-navy-700 dark:bg-navy-800"
                                            onClick={() => setExpandedId(expandedId === order._id ? null : order._id)}
                                        >
                                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{order.orderNumber}</td>
                                            <td className="px-4 py-3">{order.customerName || order.customerEmail || '-'}</td>
                                            <td className="px-4 py-3">{formatDate(order.shopifyCreatedAt)}</td>
                                            <td className="px-4 py-3">
                                                {order.totalPrice !== undefined ? `${Number(order.totalPrice).toFixed(2)} ${order.currency || ''}` : '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE[order.stockSyncStatus]}`}>
                                                    {TABS.find((t) => t.key === order.stockSyncStatus)?.label || order.stockSyncStatus}
                                                </span>
                                                {order.cancelledAt && <span className="ml-2 text-xs text-red-400">Annulée</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {(order.stockSyncStatus === 'pending' || order.stockSyncStatus === 'partial') && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            retry(order._id);
                                                        }}
                                                        disabled={retrying === order._id}
                                                        className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                                    >
                                                        {retrying === order._id ? '...' : 'Réessayer'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {expandedId === order._id && (
                                            <tr className="border-b bg-gray-50 dark:border-navy-700 dark:bg-navy-900">
                                                <td colSpan={6} className="px-4 py-3">
                                                    <table className="w-full text-xs">
                                                        <thead className="text-gray-400">
                                                            <tr>
                                                                <th className="py-1 text-left">Article</th>
                                                                <th className="py-1 text-left">SKU</th>
                                                                <th className="py-1 text-left">Qté</th>
                                                                <th className="py-1 text-left">Stock décrémenté</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(order.lineItems || []).map((li: any, idx: number) => (
                                                                <tr key={idx}>
                                                                    <td className="py-1">{li.title}</td>
                                                                    <td className="py-1">{li.sku || '-'}</td>
                                                                    <td className="py-1">{li.quantity}</td>
                                                                    <td className="py-1">
                                                                        {li.stockDecremented ? (
                                                                            <span className="text-green-600">Oui</span>
                                                                        ) : order.stockSyncStatus === 'historical' ? (
                                                                            <span className="text-blue-500">Non applicable (antérieure à la connexion)</span>
                                                                        ) : li.matchedProductId ? (
                                                                            <span className="text-amber-600">En attente</span>
                                                                        ) : (
                                                                            <span className="text-red-500">Produit non lié</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && total > 0 && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-gray-400">
                            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} sur {total} commande{total > 1 ? 's' : ''}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-navy-700 dark:text-gray-300"
                            >
                                Précédent
                            </button>
                            <span className="text-xs text-gray-500">
                                Page {page} / {totalPages}
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-navy-700 dark:text-gray-300"
                            >
                                Suivant
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </>
    );
};

export default ShopifyOrders;
